import { useEffect, useState } from 'react';
import { useT } from '../i18n/useT';
import {
  supportsFolderBackup,
  getBackupSettings,
  setBackupCadence,
  chooseBackupFolder,
  runFolderBackup,
  downloadBackupZip,
  type BackupSettings as BS,
  type BackupCadence,
} from '../lib/backup';
import { formatDateTime } from '../lib/format';

const CADENCES: BackupCadence[] = ['off', 'change', 'daily'];
const CADENCE_KEY: Record<BackupCadence, string> = {
  off: 'backup.off',
  change: 'backup.change',
  daily: 'backup.daily',
};

/** Automatic local-backup controls, shown inside Settings. */
export function BackupSettings() {
  const t = useT();
  const supported = supportsFolderBackup();
  const [s, setS] = useState<BS | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    void getBackupSettings().then(setS);
  }, []);

  const refresh = async () => setS(await getBackupSettings());
  const resultMsg = (r: Awaited<ReturnType<typeof runFolderBackup>>): string =>
    r.ok
      ? t('backup.done', { n: r.count })
      : r.reason === 'permission'
        ? t('backup.permission')
        : t('backup.failed');

  return (
    <div className="mb-setting mb-setting-block">
      <label>{t('backup.title')}</label>
      <div className="mb-backup">
        {supported ? (
          <>
            <div className="mb-backup-row">
              <span className="mb-backup-folder">
                {s?.folderName
                  ? t('backup.folderIs', { name: s.folderName })
                  : t('backup.noFolder')}
              </span>
              <button
                className="mb-btn"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  const next = await chooseBackupFolder();
                  if (next) {
                    setS(next);
                    setMsg(resultMsg(await runFolderBackup(true)));
                    await refresh();
                  }
                  setBusy(false);
                }}
              >
                {t('backup.choose')}
              </button>
            </div>
            <div className="mb-backup-row">
              {/* A real label, not a span beside the select: without it a screen
                  reader announced an unnamed combo box. */}
              <label htmlFor="mb-backup-cadence">{t('backup.cadence')}</label>
              <select
                id="mb-backup-cadence"
                className="mb-sort-select"
                value={s?.cadence ?? 'off'}
                onChange={async (e) => {
                  await setBackupCadence(e.target.value as BackupCadence);
                  await refresh();
                }}
              >
                {CADENCES.map((c) => (
                  <option key={c} value={c}>
                    {t(CADENCE_KEY[c])}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-backup-row">
              <button
                className="mb-btn"
                disabled={busy || !s?.folderName}
                onClick={async () => {
                  setBusy(true);
                  setMsg(resultMsg(await runFolderBackup(true)));
                  await refresh();
                  setBusy(false);
                }}
              >
                {t('backup.now')}
              </button>
              <span className="mb-backup-last">
                {s?.lastBackupAt
                  ? t('backup.last', { date: formatDateTime(s.lastBackupAt) })
                  : t('backup.never')}
              </span>
            </div>
          </>
        ) : (
          <p className="mb-modal-note">{t('backup.unsupported')}</p>
        )}
        <button
          className="mb-btn"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await downloadBackupZip();
            setBusy(false);
          }}
        >
          {t('backup.zip')}
        </button>
        {msg && <p className="mb-backup-msg">{msg}</p>}
      </div>
    </div>
  );
}
